// Records a care-plan task as performed (e.g. a medication dose given). The
// form is not hard-coded — it is rendered from the task's fulfillment_form,
// which the backend inlines from service_fulfillment_type.structure. So the
// same modal records a medication administration, a wound dressing, a
// nebulization, etc., each with its own fields, with no per-type UI code.
//
// Submits through fulfill_care_plan_task/ (action "complete"), which stores the
// filled form on the task and rolls the parent care plan's status forward.
import React, { useMemo, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, Divider, Chip,
} from "@mui/material";
import { toast } from "react-toastify";

import { fulfillCarePlanTask } from "../api/nurseApi";
import { NURSE_COLORS } from "../theme";
import {
  fieldsOf, labelize, initialValues, firstMissingRequired, serialize, FieldInput,
} from "./fulfillmentForm";
import { taskHeading, orderDetailLines } from "../taskDisplay";
import { useFulfillmentNames } from "../hooks";

// A single order detail worth surfacing so the nurse sees WHAT they're recording
// against (drug, route, frequency) without opening the order. Non-medication
// tasks get the same treatment from their own care-plan fields — before
// DEFIPD082 they showed nothing here at all, because only the medication shape
// was read.
function OrderSummary({ task, orderDetails, taskType }) {
  const med = orderDetails?.medication;
  const bits = [];
  if (med?.drug_name) bits.push(med.drug_name);
  if (med?.route) bits.push(med.route);
  if (med?.frequency) bits.push(med.frequency);
  const line = bits.join(" · ");
  const detailLines = line ? [] : orderDetailLines(task);
  if (!line && !detailLines.length) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary">Ordered</Typography>
      {line ? (
        <Typography variant="body2" sx={{ fontWeight: 600 }}>{line}</Typography>
      ) : (
        detailLines.map((detail) => (
          <Typography key={detail} variant="body2">{detail}</Typography>
        ))
      )}
    </Box>
  );
}

// The pre-receipt source prompt that used to live here is gone. It was meant to
// fire only when meds had not yet arrived on the ward, but nothing in the
// system ever marks an order Received — so it fired on EVERY dose and blocked
// each one behind a mandatory dropdown that distinguished nothing. Received
// meds sit in ward stock under the patient's name; the EMR simply never learns
// it. Where a dose came from stays recordable via the form's own fields.
export default function TaskFulfillmentModal({ open, task, performedById, onClose, onFulfilled }) {
  const fields = useMemo(() => fieldsOf(task?.fulfillment_form), [task]);
  const fulfillmentNames = useFulfillmentNames();
  const [values, setValues] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reseed whenever a new task opens.
  const taskId = task?.id;
  React.useEffect(() => {
    if (open && fields.length) setValues(initialValues(fields));
  }, [open, taskId, fields]);

  const setTop = (field, val) => setValues((s) => ({ ...s, [field]: val }));
  const setNested = (parent, field, val) =>
    setValues((s) => ({ ...s, [parent]: { ...(s[parent] || {}), [field]: val } }));

  const handleSubmit = async () => {
    const missing = firstMissingRequired(fields, values);
    if (missing) {
      toast.error(`Please fill: ${missing}`);
      return;
    }
    if (task?.id == null) {
      toast.error("This task has no ID and can't be recorded.");
      return;
    }
    setSubmitting(true);
    try {
      const data = serialize(fields, values);
      const res = await fulfillCarePlanTask({
        task_id: task.id,
        performed_by_id: performedById,
        action: "complete",
        data,
      });
      if (res?.status === "Success") {
        toast.success("Recorded.");
        onFulfilled?.();
        onClose();
      } else {
        toast.error(res?.message || "Could not record this task.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not record this task.");
    } finally {
      setSubmitting(false);
    }
  };

  const noForm = fields.length === 0;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 600 }}>
        Record: {taskHeading(task, fulfillmentNames)}
        {task?.time_slot_label ? (
          <Chip size="small" label={task.time_slot_label} sx={{ ml: 1 }} />
        ) : null}
      </DialogTitle>
      <DialogContent dividers>
        <OrderSummary task={task} orderDetails={task?.order_details} taskType={task?.task_type} />
        {task?.patient?.name && (
          <Typography variant="caption" color="text.secondary">
            {task.patient.name}{task.patient.bed_code ? ` · ${task.patient.bed_code}` : ""}
          </Typography>
        )}
        <Divider sx={{ my: 1.5 }} />
        {noForm ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No fulfillment form is configured for this task. Marking it done will
            record only who did it and when.
          </Typography>
        ) : (
          <Stack spacing={1.75} sx={{ mt: 0.5 }}>
            {fields.map((f) =>
              f.type === "object" ? (
                <Box key={f.field} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>{labelize(f.field)}</Typography>
                  <Stack spacing={1.5}>
                    {(f.structure || []).map((sub) => (
                      <FieldInput
                        key={sub.field}
                        def={sub}
                        value={(values[f.field] || {})[sub.field]}
                        onChange={(field, val) => setNested(f.field, field, val)}
                      />
                    ))}
                  </Stack>
                </Box>
              ) : (
                <FieldInput key={f.field} def={f} value={values[f.field]} onChange={setTop} />
              )
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
          {submitting ? "Recording…" : "Record"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
